import { LitElement, html, css, type PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { CONTAINER_MAX } from '../tokens.js';
import { resolvePad } from '../utils.js';

/**
 * uwc-container — Max-width page wrapper
 *
 * Centers and constrains content to a readable maximum width. Always sits at
 * the outermost edge of a page section; place `uwc-stack` or `uwc-grid`
 * inside it, not the other way around.
 *
 * Philosophy: `uwc-container` handles only the outer constraint and centering;
 * it has no opinion on how its children are arranged. Pair it with
 * `uwc-stack` or `uwc-grid` for any internal layout.
 *
 * Size tokens  →  max-width
 *   xs   480px   sm   640px   md   768px
 *   lg  1024px   xl  1280px   2xl 1536px   full 100%
 *
 * @attr {string}  size   - Max-width token or any CSS value. Default: xl (1280px)
 * @attr {boolean} center - Auto-center with margin:auto. Default: true
 * @attr {string}  pad    - Horizontal + vertical padding token or CSS value
 *
 * @cssprop --uwc-container-max-width - Override max-width directly
 * @cssprop --uwc-container-px        - Override horizontal padding
 */
export class UwcContainer extends LitElement {
  static override styles = css`
    :host {
      display:        block;
      width:          100%;
      min-width:      0;
      max-width:      var(--uwc-container-max-width, var(--_max-width, 1280px));
      margin-left:    var(--_ml,  auto);
      margin-right:   var(--_mr,  auto);
      padding-left:   var(--uwc-container-px, var(--_px, 1rem));
      padding-right:  var(--uwc-container-px, var(--_px, 1rem));
      padding-top:    var(--_py,  0);
      padding-bottom: var(--_py,  0);
      box-sizing:     border-box;
    }
  `;

  /** Max-width size token or CSS value. */
  @property({ type: String })  size   = 'xl';
  /** Auto-center with margin: auto. */
  @property({ type: Boolean }) center = true;
  /** Uniform padding token or CSS value applied to all four sides. */
  @property({ type: String })  pad    = '';

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('size')) {
      const maxW = CONTAINER_MAX[this.size] ?? this.size ?? '1280px';
      this.style.setProperty('--_max-width', maxW);
    }
    if (changed.has('center')) {
      const m = this.center ? 'auto' : '0';
      this.style.setProperty('--_ml', m);
      this.style.setProperty('--_mr', m);
    }
    if (changed.has('pad')) {
      const p = resolvePad(this.pad);
      this.style.setProperty('--_px', p);
      this.style.setProperty('--_py', p);
    }
  }

  override render() { return html`<slot></slot>`; }
}
